<template>
  <ThePrimeModal
    :start_maximized="true"
    :visible_prop="true"
    :is_loading="is_saving"
    :hide_maximize_button="true"
    :data_test_primary="'region-save'"
    @show="onModalShow"
    @action-cancel="onModalHide"
    @action-primary="saveSignaturePositions"
  >
    <template #title>
      <div class="title is-4">
        {{ $t('signature.labels.place_signature_title') }}
      </div>
    </template>
    <template #subtitle>
      <div class="subtitle is-5">
        {{ $t('signature.labels.place_signature_subtitle') }}
      </div>
    </template>
    <template #notification_danger>
      <Notice
        v-if="errorMsg"
        data-test="notice-warning"
      >
        {{ errorMsg }}
      </Notice>
    </template>
    <template #content>
      <div class="flex w-full justify-center">
        <div
          id="canvas-modal"
          class="mx-auto"
        >
          <div>
            <section class="border-t border-gray-400">
              <div
                data-test="document-page-canvas"
                class="relative"
                :class="{ 'pointer-events-none': isLoading }"
              >
                <canvas id="canvas" />
              </div>
            </section>
            <BLoading
              :is-full-page="false"
              :model-value="ui_disabled"
            />
            <div
              v-if="is_pdf && pdfPagesCount > 1"
              class="fixed bottom-17 left-0 z-20 flex h-12 w-full items-center justify-center bg-gray-300"
            >
              <div class="flex items-center">
                <div class="flex size-8 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100">
                  <FontAwesomeIcon
                    data-test="arrow-left"
                    :class="{ invisible: page === 1 }"
                    :icon="['far', 'arrow-left']"
                    @click="changePage(page - 1)"
                  />
                </div>

                <b-select
                  class="mx-1 inline-flex"
                  :disabled="ui_disabled"
                  :modelValue="page"
                  @update:modelValue="changePage"
                >
                  <option
                    v-for="p in page_numbers"
                    :key="p"
                    :value="p"
                  >
                    {{ p }}
                  </option>
                </b-select>
                <div class="flex size-8 cursor-pointer items-center justify-center rounded-full hover:bg-gray-100">
                  <FontAwesomeIcon
                    data-test="arrow-right"
                    :class="{ invisible: page >= pdfPagesCount }"
                    :icon="['far', 'arrow-right']"
                    @click="changePage(page + 1)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #ButtonPrimary>
      {{ $t('signature.actions.save') }}
    </template>
  </ThePrimeModal>
</template>

<script>
import { mapActions } from 'vuex';
import { PROFILE_TYPES, RECORD_TYPE } from '@/app/data/model_constants';
import Notice from '@/app/shared_components/notices/Notice.vue';
import useSignatureCanvas from '@/composables/useSignatureCanvas.js';
import {
  DEFAULT_SIGNATURE_WIDTH,
  MAX_SIGNATURES_PER_PAGE,
  SIGNATURE_PAD_RATIO,
} from '@/app/data/signature_constants.js';
import ThePrimeModal from '@/app/shared_components/modals/ThePrimeModal.vue';

export default {
  name: 'SignatureIndicatorModal',
  components: { ThePrimeModal, Notice },
  props: {
    document_prop: {
      type: Object,
      required: true,
    },
    signatures_prop: {
      type: Array,
      default: () => [],
    },
    project: {
      type: Object,
      required: true,
    },
  },

  emits: ['close', 'attachmentSignedCreated'],

  setup() {
    const {
      fabricCanvasInstance,
      page,
      pdfPagesCount,
      isLoading,
      errorMsg,
      docDimensions,
      setupFabric,
      renderDocument,
      createSignatureRect,
    } = useSignatureCanvas();

    return {
      fabricCanvasInstance,
      page,
      pdfPagesCount,
      isLoading,
      errorMsg,
      docDimensions,
      setupFabric,
      renderDocument,
      createSignatureRect,
    };
  },

  data() {
    return {
      signatures: [],
      signatory_type: PROFILE_TYPES.CUSTOMER,
      is_saving: false,
      PROFILE_TYPES,
      canvas_initialized: false,
      pending_add_promise: null,
    };
  },

  computed: {
    is_pdf() {
      return this.document_prop.attributes.file_mime_type.startsWith('application/pdf');
    },
    page_signatures() {
      return _.filter(this.signatures, ['attributes.page', this.page]);
    },
    page_numbers() {
      return _.range(1, this.pdfPagesCount + 1);
    },
    ui_disabled() {
      return this.isLoading || this.is_saving;
    },
    attachment_id() {
      return this.document_prop.type === RECORD_TYPE.ATTACHMENT
        ? this.document_prop.id
        : this.document_prop.attributes.attachment_id;
    },
  },

  created() {
    if (this.signatures_prop.length) {
      this.signatures = _.cloneDeep(this.signatures_prop);
    }
  },

  methods: {
    ...mapActions(['CREATE_SIGNATURES', 'FETCH_SIGNATURES']),

    onModalHide() {
      this.$emit('close');
    },

    async onModalShow() {
      if (this.canvas_initialized) return;
      await this.$nextTick();
      this.setupFabric('canvas');
      const doc_url = this.document_prop.attributes.file_url;

      if (!doc_url) {
        this.errorMsg = 'Document URL is missing.';
        return;
      }

      await this.loadCurrentPage();
      this.canvas_initialized = true;
    },

    changePage(pageNum) {
      this.syncCanvasToLocalState();
      this.page = pageNum;
      this.errorMsg = false;

      if (this.fabricCanvasInstance) {
        this.fabricCanvasInstance.remove(...this.fabricCanvasInstance.getObjects());
        this.loadCurrentPage();
      }
    },

    async loadCurrentPage() {
      const doc_url = this.document_prop.attributes.file_url;

      await this.renderDocument(doc_url, this.page, () => {
        this.setupCanvasInteractions();
        this.renderExistingSignatures();
      });
    },

    setupCanvasInteractions() {
      this.fabricCanvasInstance.off('mouse:down');
      this.fabricCanvasInstance.on('mouse:down', (options) => {
        if (!options.target) {
          const pointer = this.fabricCanvasInstance.getPointer(options.e);
          const p = this.handleAddSignatureRequest(pointer.x, pointer.y).finally(() => {
            if (this.pending_add_promise === p) this.pending_add_promise = null;
          });
          this.pending_add_promise = p;
        }
      });
    },

    renderExistingSignatures() {
      _.each(this.page_signatures, (signature) => this.handleAddSignatureRequest(null, null, signature));
    },

    async handleAddSignatureRequest(x, y, signature = null) {
      if (!signature && this.fabricCanvasInstance) {
        const count_signatures_page = this.fabricCanvasInstance.getObjects().length;
        if (count_signatures_page >= MAX_SIGNATURES_PER_PAGE) {
          return;
        }
      }

      this.errorMsg = false;

      let height, left, top, width;
      const minW = DEFAULT_SIGNATURE_WIDTH;
      const minH = minW / SIGNATURE_PAD_RATIO;

      const docW = this.docDimensions.width;
      const docH = this.docDimensions.height;

      if (signature) {
        width = (signature.attributes.width * docW) / 100;
        height = (signature.attributes.height * docH) / 100;
        left = (signature.attributes.left * docW) / 100;
        top = (signature.attributes.top * docH) / 100;
      } else {
        width = minW;
        height = minH;
        if (x && y) {
          left = x - width / 2;
          top = y - height / 2;
        } else {
          left = docW - width - docW / 5;
          top = docH - height - docH / 3;
        }
      }

      const currentSigType = signature ? signature.attributes.signatory_type : this.signatory_type;
      const isSelectable = _.isEmpty(signature) || currentSigType === this.signatory_type;

      const rect = await this.createSignatureRect(
        { left, top, width, height, signatoryType: currentSigType, isSelectable },
        {
          onDelete: () => {
            this.errorMsg = false;
            this.syncCanvasToLocalState();
          },
          onUpdate: () => {},
        },
      );

      if (this.signatory_type === rect.signatory_type) {
        this.fabricCanvasInstance.setActiveObject(rect);
      }

      if (!signature) {
        this.addSignatureToLocalArray(rect);
      }
    },

    addSignatureToLocalArray(rect) {
      const width = rect.width * rect.scaleX;
      const height = rect.height * rect.scaleY;

      const newSig = {
        attributes: {
          project_id: this.project.id,
          page: this.page,
          width: (width * 100) / this.docDimensions.width,
          height: (height * 100) / this.docDimensions.height,
          left: (rect.left * 100) / this.docDimensions.width,
          top: (rect.top * 100) / this.docDimensions.height,
          signatory_type: rect.signatory_type,
        },
      };

      if (_.isEmpty(this.signatures_prop)) {
        newSig.attributes.base_attachment_id = this.attachment_id;
      } else {
        newSig.attributes.attachment_id = this.attachment_id;
      }
      this.signatures.push(newSig);
    },

    syncCanvasToLocalState() {
      this.signatures = _.filter(this.signatures, (s) => s.attributes.page !== this.page);

      if (!this.fabricCanvasInstance) return;
      const rects = this.fabricCanvasInstance.getObjects();

      _.each(rects, (rect) => this.addSignatureToLocalArray(rect));
    },

    async saveSignaturePositions() {
      if (this.pending_add_promise) await this.pending_add_promise;
      this.syncCanvasToLocalState();

      if (_.isEmpty(this.signatures)) {
        this.errorMsg = this.$t('signature.labels.no_signature_indicators_warning');
        return;
      }
      this.is_saving = true;

      try {
        const signatures = await this.CREATE_SIGNATURES({ objects: this.signatures });

        if (_.isEmpty(this.signatures_prop)) {
          this.$emit('attachmentSignedCreated', signatures[0].attributes.attachment_id);
        }
        await this.FETCH_SIGNATURES({ attachment_id: this.attachment_id });

        this.$emit('close');
      } catch (e) {
        this.errorMsg = 'An error occurred while saving.';
      } finally {
        this.is_saving = false;
      }
    },
  },
};
</script>
<style scoped>
#canvas-modal .p-dialog-content {
  padding-bottom: 0 !important;
}
</style>
